import {
    type MouseEvent,
    type FC,
    useMemo,
    type MutableRefObject,
    type ReactNode,
    type CSSProperties,
} from 'react';
import classNames from 'classnames';

//components
import { StyledButton, type ButtonTypesType } from '../Common/Button/StyledButton.tsx';

// colors
import { type SassyColorNames, colors } from 'dibs-sassy/exports/colors.ts';

// styles
import styles from './main.scss';
export type CustomButtonColors = {
    background?: SassyColorNames;
    hoverBackground?: SassyColorNames;
    text?: SassyColorNames;
    border?: SassyColorNames;
    disabledBackground?: SassyColorNames;
    disabledText?: SassyColorNames;
};

/** @deprecated Use "standard" or "subtle" instead. Kept for backward compatibility. */
type LegacyButtonTypes = 'secondary' | 'textCta' | 'transparent';

type ButtonTypes = 'primary' | 'primaryAlt' | 'secondaryAlt' | 'standard' | 'subtle';
export type ButtonStyles = 'boxy' | 'rounded' | 'circle';

export type ButtonProps = {
    className?: string;
    children?: ReactNode;
    dataTn?: string;
    disabled?: boolean;
    fullWidth?: boolean;
    href?: string;
    isDangerousAction?: boolean;
    noFollow?: boolean;
    onClick?: (e: MouseEvent<HTMLElement>) => void;
    opensInNewWindow?: boolean;
    size?: 'small' | 'medium' | 'large';
    target?: string;
    /** 'secondary', 'textCta', and 'transparent' are deprecated, use "standard" or "subtle" instead */
    type?: ButtonTypes | LegacyButtonTypes;
    htmlType?: ButtonTypesType;
    ariaLabel?: string;
    role?: 'menuitem';
    buttonRef?:
        | ((element: HTMLElement | null) => void)
        | MutableRefObject<HTMLButtonElement | HTMLAnchorElement | null>;
    /**
     * `wrap` does not affect Buttons with HTML element descendants. `wrap` allows line-wrapping on
     * Buttons by applying inline-flex, line-height and white-space rules. disable to use legacy
     * inline-block line-height = Button height layout. please note that Buttons do not have include
     * any overflow rules.
     * @default true
     */
    wrap?: boolean;
    form?: string;
    style?: ButtonStyles;
    customColors?: CustomButtonColors;
};

export const Button: FC<ButtonProps> = ({
    children,
    dataTn,
    className,
    disabled,
    fullWidth,
    href,
    isDangerousAction,
    noFollow,
    onClick = () => {},
    opensInNewWindow,
    size = 'large',
    target,
    type = 'primary',
    htmlType,
    ariaLabel,
    buttonRef,
    wrap = true,
    form,
    style = 'boxy',
    customColors,
}) => {
    if (
        ![
            'primary',
            'primaryAlt',
            'secondary',
            'secondaryAlt',
            'textCta',
            'transparent',
            'standard',
            'subtle',
        ].includes(type) &&
        process.env.NODE_ENV !== 'production'
    ) {
        // eslint-disable-next-line no-console
        console.warn(
            `dibs-elements | Button | Type Error: You have passed in "${type}" for prop "type". Only 'primary', 'primaryAlt', 'secondary', 'secondaryAlt', 'textCta', 'transparent', 'standard', or 'subtle' is allowed. Did you mean to use "htmlType"?`
        );
    }
    const isPrimary = ![
        'primaryAlt',
        'secondary',
        'secondaryAlt',
        'textCta',
        'transparent',
        'standard',
        'subtle',
    ].includes(type);

    const addStyle = style && ['primary', 'standard', 'subtle'].includes(type);

    const hasCustomColors = !!customColors && Object.values(customColors).some(Boolean);

    const outputClassName = classNames(
        {
            [styles.primaryAltButton]: type === 'primaryAlt',
            [styles.secondaryButton]: type === 'secondary',
            [styles.secondaryAltButton]: type === 'secondaryAlt',
            [styles.transparentButton]: type === 'transparent',
            [styles.textCtaButton]: type === 'textCta',
            [styles.standardButton]: type === 'standard',
            [styles.subtleButton]: type === 'subtle',
            [styles.isDangerousActionPrimary]: isDangerousAction && type === 'primary',
            [styles.isDangerousActionSecondary]:
                isDangerousAction && type !== 'primary' && type !== 'transparent',
            [styles.isDangerousActionTransparent]: isDangerousAction && type === 'transparent',
            [styles.fullWidth]: fullWidth,
            [styles.disabled]: disabled,
            [styles.noWrap]: !wrap,
            [styles.wrap]: wrap,
            [styles.rounded]: style === 'rounded',
            [styles.circle]: style === 'circle',
            ...(hasCustomColors && {
                [styles.customColors]: true,
                [styles.customColorsBackground]: !!customColors.background,
                [styles.customColorsHoverBackground]: !!customColors.hoverBackground,
                [styles.customColorsText]: !!customColors.text,
                [styles.customColorsBorder]: !!customColors.border,
                [styles.customColorsDisabledBackground]: !!customColors.disabledBackground,
                [styles.customColorsDisabledText]: !!customColors.disabledText,
            }),
        },
        styles[size],
        className
    );

    const cssStyles = useMemo(() => {
        if (!customColors) {
            return undefined;
        }

        return {
            ...(customColors?.background && {
                ['--btn-custom-bg-color']: colors[customColors.background],
            }),
            ...(customColors?.hoverBackground && {
                ['--btn-custom-hover-bg-color']: colors[customColors.hoverBackground],
            }),
            ...(customColors?.text && {
                ['--btn-custom-text-color']: colors[customColors.text],
            }),
            ...(customColors?.border && {
                ['--btn-custom-border-color']: colors[customColors.border],
            }),
            ...(customColors?.disabledBackground && {
                ['--btn-custom-disabled-bg-color']: colors[customColors.disabledBackground],
            }),
            ...(customColors?.disabledText && {
                ['--btn-custom-disabled-text-color']: colors[customColors.disabledText],
            }),
        } as CSSProperties;
    }, [customColors]);

    const optionalProps: {
        disabled?: boolean;
        target?: string;
        rel?: string;
        type?: ButtonTypesType;
        ariaLabel?: string;
        cssStyle?: CSSProperties;
    } = { ariaLabel };
    if (disabled) {
        optionalProps.disabled = true;
    }
    optionalProps.cssStyle = cssStyles;
    if (typeof href === 'string') {
        if (opensInNewWindow) {
            optionalProps.target = '_blank';
            optionalProps.rel = 'noopener'; // https://developers.google.com/web/tools/lighthouse/audits/noopener
        }
        if (noFollow) {
            if (optionalProps.rel) {
                optionalProps.rel += ' nofollow';
            } else {
                optionalProps.rel = 'nofollow';
            }
        }
        if (target) {
            optionalProps.target = target;
        }
        return (
            <StyledButton
                className={outputClassName}
                dataTn={dataTn}
                href={href}
                isPrimary={isPrimary}
                onClick={onClick}
                buttonRef={buttonRef}
                style={addStyle ? style : undefined}
                {...optionalProps}
            >
                {children}
            </StyledButton>
        );
    }
    if (htmlType) {
        optionalProps.type = htmlType;
    }

    return (
        <StyledButton
            className={outputClassName}
            dataTn={dataTn}
            isPrimary={isPrimary}
            onClick={onClick}
            buttonRef={buttonRef}
            form={form}
            style={addStyle ? style : undefined}
            {...optionalProps}
        >
            {children}
        </StyledButton>
    );
};
